import getConfig from 'next/config';
import { handleTrack } from '../../../../../utils/tracker';
import { ButtonVariants } from '../../../../Atoms/Button';
import { ButtonWithAnchor } from '../../../../income-tax/page-sections/hero-section/sticky-button';
import { IService } from '../../../../partner-pages/types';
import useCopy from '../../../../partner-pages/utils/hooks/use-copy';
import { sanitizeRichText } from '../../../../partner-pages/utils/textUtils';
import { addCommasToNumber } from '../../../../Utils/CommonUtils';
import { TPricingPlans } from '../../types';
import { Tooltip } from '../../../../Atoms/Tooltip';
import { PricingCardStaticDetailsTypesV1 } from './card-details-constants-v1';

interface PricingCardProps {
  card: PricingCardStaticDetailsTypesV1 | undefined;
  pricingPlanData: TPricingPlans;
}

const { publicRuntimeConfig } = getConfig();

export const PricingCardV1 = ({ card, pricingPlanData }: PricingCardProps) => {
  const baseUrl = publicRuntimeConfig.CLEARTAX_DOMAIN;
  const planDetails = pricingPlanData?.planDetails;
  const delay = 1000;

  const { copied, handleCopyCoupon } = useCopy(
    pricingPlanData?.autoApplicableCoupon?.code || '',
    delay
  );

  const BUY_NOW_LINK = `${baseUrl}/checkout/${planDetails?.listingId}${
    pricingPlanData?.autoApplicableCoupon?.code
      ? '?coupon=' + pricingPlanData?.autoApplicableCoupon?.code
      : ''
  }`;

  const KNOW_MORE_LINK = `${baseUrl}/services/${pricingPlanData.externalId}/p`;

  const displayName: string = planDetails?.name?.split(' - ')?.[1] ?? '';
  const isPremium = displayName?.trim()?.toLowerCase() === 'premium';

  const redirectionLink = `${baseUrl}/checkout/${planDetails?.listingId}${
    pricingPlanData?.autoApplicableCoupon?.code
      ? `?coupon=${pricingPlanData?.autoApplicableCoupon?.code}&utm_ref=plan_partner_page_card`
      : '?utm_ref=plan_partner_page_card'
  }`;

  const notch =
    'https://assets.clear.in/cleartax/images/1751879976_subtract.svg';

  return (
    <div
      className={`relative sm:w-full rounded-2xl overflow-hidden flex flex-col justify-between
        ${isPremium ? 'border-2 border-[#23B26D]' : 'border border-gray-300'}`}
      style={{ background: card?.bgColor }}
    >
      {isPremium && (
        <span className="absolute top-0 left-0 z-10 bg-green-700 text-white px-4 py-1 rounded-br-lg text-s-14 font-medium">
          Popular
        </span>
      )}

      <a
        href={redirectionLink}
        className="text-black"
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => {
          handleTrack({
            label: 'plan-card_redirection',
            url: redirectionLink,
            obj: e.target,
            value: planDetails?.name,
          });
        }}
      >
        <div className="top-section flex justify-between font-medium gap-6">
          <div
            className="pl-6 pt-3 pb-4 sm:pl-4 sm:pb-6 bg-no-repeat w-full bg-right-top"
            style={{
              backgroundImage: card?.topSectionImg
                ? `url(${card?.topSectionImg})`
                : 'none',
            }}
          >
            <div className="font-semibold mt-5 text-s-24 sm:text-s-20">
              {planDetails?.name?.split(' - ')?.[1]}
            </div>
            <div className="text-base font-medium text-[#314259] pr-6 sm:pr-5 sm:text-s-14">
              {planDetails?.shortDescription}
            </div>
            <div className="mt-4">
              <div className="flex justify-between w-full">
                <div className="text-s-14 text-font-200">
                  {planDetails?.name === 'Cleartax Assisted Filing - Luxury'
                    ? 'Starting at'
                    : 'Just at'}
                </div>
                <div className="flex">
                  <div className="font-semibold pr-3 text-s-24 leading-none sm:text-s-24 order-1">
                    ₹
                    {addCommasToNumber(
                      Math.floor(planDetails?.pricing?.discountedPrice || 0)
                    )}
                  </div>
                  {planDetails?.pricing?.listPrice >
                    planDetails?.pricing?.discountedPrice && (
                    <div className="flex justify-start items-center gap-2 mt-1 pr-2">
                      <div className="text-s-14 text-font-200 line-through">
                        ₹{addCommasToNumber(planDetails?.pricing?.listPrice)}
                      </div>
                      {/* <div className="text-s-14 text-purple-300">
                    {discountValue}
                  </div> */}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </a>

      {pricingPlanData?.autoApplicableCoupon && (
        <div
          className="relative flex items-center justify-between px-4 py-2 sm:py-1.5 sm:px-3 text-s-14 font-medium text-black"
          style={{
            backgroundImage: `url(${notch})`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
          }}
          onClick={() => {
            handleCopyCoupon();
            handleTrack({
              label: `plan-card_copy`,
              obj: null,
              valueObj: { pricingPlanData: planDetails?.name },
            });
          }}
        >
          <div className="relative z-10 flex justify-center items-center w-full">
            <div className="text-s-12 font-bold sm:text-s-12 whitespace-nowrap">
              Get {pricingPlanData?.autoApplicableCoupon?.discountValue}% OFF
            </div>
            <div className="h-5 border-r border-dashed border-[#CBD5E1] mx-2"></div>
            <div className="flex items-center gap-1">
              <span className="text-s-12 sm:text-s-12 text-font-200">
                Code:
              </span>
              <span className="text-s-12 sm:text-s-12 text-blue-350 font-medium">
                {pricingPlanData?.autoApplicableCoupon?.code}
              </span>
              <img
                className="w-4 h-4 ml-1"
                src={
                  copied
                    ? 'https://assets.clear.in/cleartax/images/1712001391_checkcircle1.svg'
                    : 'https://assets.clear.in/cleartax/images/1714203952_copy.svg'
                }
                alt="copy icon"
              />
            </div>
          </div>
        </div>
      )}

      <div className="middle-section bg-[#FFF] flex flex-col flex-grow">
        <div className="px-5 pt-4">
          {planDetails?.whenRequired?.map((features: IService) => {
            const sanitizedRichText = sanitizeRichText(features?.richText);
            const tooltip = features?.tooltip;

            return (
              <div
                key={features?.id}
                className="flex gap-2 items-start relative"
              >
                <img
                  className="w-3 h-7  flex-shrink-0"
                  src="https://assets.clear.in/cleartax/images/1751960042_frame2147224398.svg"
                  alt="checkmark"
                />

                {tooltip ? (
                  <Tooltip
                    customClassName="bg-brand-1000 text-white p-4 rounded-xl shadow-xl max-w-sm z-10 mt-14"
                    content={
                      <div
                        className="text-s-14 text-white"
                        dangerouslySetInnerHTML={{ __html: tooltip }}
                      />
                    }
                    direction="top"
                    showOnHover
                    contentPosition="absolute"
                    hideCloseIcon
                  >
                    <span className="inline-flex items-center gap-1">
                      <span
                        className="text-font-500 text-s-base font-medium underline decoration-dotted sm:text-s-14"
                        dangerouslySetInnerHTML={{ __html: sanitizedRichText }}
                      />
                    </span>
                  </Tooltip>
                ) : (
                  <span
                    className="text-font-500 text-s-base font-medium sm:text-s-14"
                    dangerouslySetInnerHTML={{ __html: sanitizedRichText }}
                  />
                )}
              </div>
            );
          })}
        </div>

        {planDetails?.servicesIncluded?.length > 0 && (
          <>
            <hr className="my-3" />
            <div className="flex-grow">
              {planDetails?.servicesIncluded?.map((features: IService) => {
                const sanitizedRichText = sanitizeRichText(features?.richText);
                return (
                  <div
                    key={features?.id}
                    className="px-5 py-2 text-s-base sm:text-s-14"
                  >
                    <div
                      dangerouslySetInnerHTML={{ __html: sanitizedRichText }}
                      className="
                        [&>p]:text-s-base
                        [&>p]:font-medium
                        [&>p]:text-black 
                      "
                    />
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      <div className="mt-auto px-4 pb-6 pt-2">
        <ButtonWithAnchor
          trackLabel="plan-card"
          buttonText="Buy Now"
          buttonVariant={ButtonVariants.bluefilled}
          className="text-base px-2 py-2"
          buttonLink={planDetails?.buyLink || BUY_NOW_LINK}
          value={planDetails?.buyLink || BUY_NOW_LINK}
          target="_blank"
        />
        <ButtonWithAnchor
          trackLabel="plan-card"
          buttonText="Know more"
          buttonVariant={ButtonVariants.bluetext}
          className="text-base w-full focus:outline-none active:outline-none"
          buttonLink={planDetails?.knowLink || KNOW_MORE_LINK}
          value={planDetails?.knowLink || BUY_NOW_LINK}
          target="__blank"
        />
      </div>
    </div>
  );
};
